import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { Freigabe } from './freigabe';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../services/message-service/message.service';
import { MatDialog } from '@angular/material/dialog';
import { FreigabeErstellenDialogComponent } from './freigabe-erstellen-dialog/freigabe-erstellen-dialog.component';
import { AppService } from '../../../services/app/app.service';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../dialogs/confirm-dialog/confirm-dialog';
import { HttpParams } from '@angular/common/http';
import { FormControl } from '@angular/forms';
// Stripe integration disabled — import, injection, and removeFreigabe() call commented out below.
// import { PhitoStripeService } from '../../../services/phito-stripe.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';

@Component({
    selector: 'kiene-freigaben',
    templateUrl: './freigaben.component.html',
    styleUrls: ['./freigaben.component.scss'],
})
export class FreigabenComponent implements OnInit {
    @Input('showReturnButton') showReturnButton = true;
    @Output('freigabenChanged') freigabenChanged = new EventEmitter<number>();
    isMobile = false;

    freigaben: Freigabe[] = [];
    geloeschteFreigaben: Freigabe[] = [];
    searchControl = new FormControl('');
    sortField: 'datum' | 'betrieb' | 'freigegebenFuer' = 'datum';
    sortDirection: 'asc' | 'desc' = 'desc';

    constructor(
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneBackendApiService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private appService: AppService,
        // private stripeService: PhitoStripeService,
        private localStorageService: LocalStorageService,
    ) {
        this.isMobile = this.appService.isMobileScreen;
    }

    ngOnInit(): void {
        this.ladeFreigaben();
    }

    freigabeAnlegen() {
        this.dialog
            .open(FreigabeErstellenDialogComponent, { width: '100%', minWidth: this.isMobile ? '99vw' : '600px' })
            .afterClosed()
            .subscribe({
                next: saved => {
                    if (saved) {
                        this.ladeFreigaben();
                    }
                },
            });
    }

    editFreigabe(f: Freigabe) {
        this.dialog
            .open(FreigabeErstellenDialogComponent, {
                width: '100%',
                minWidth: this.isMobile ? '99vw' : '600px',
                data: { freigabe: f },
            })
            .afterClosed()
            .subscribe({
                next: saved => {
                    if (saved) {
                        this.ladeFreigaben();
                    }
                },
            });
    }

    deleteFreigabe(f: Freigabe) {
        const inp = new ConfirmDialogInput();
        inp.headline = 'Freigabe entfernen';
        inp.text = 'Wollen Sie die Freigabe wirklich entfernen?';
        inp.okButton = 'entfernen';
        this.dialog
            .open(ConfirmDialogComponent, { data: inp })
            .afterClosed()
            .subscribe({
                next: (result: ConfirmDialogResult) => {
                    if (result.ok && result.confirmed) {
                        this.api.deleteEntity(this.apiBaseUrl + 'kunde_freigabe/delete.php', f).subscribe({
                            next: () => {
                                // this.stripeService
                                //     .removeFreigabe(f.freigeber === 1 ? f.kunde_id_freigabe : null)
                                //     .subscribe({
                                //         next: () => {
                                //             this.ladeFreigaben();
                                //         },
                                //         error: error => {
                                //             this.messageService.errorMessage('Fehler!', error);
                                //         },
                                //     });
                                this.ladeFreigaben();
                            },
                            error: err => {
                                this.messageService.errorMessage('Fehler!', err);
                            },
                        });
                    }
                },
            });
    }

    private ladeFreigaben() {
        this.api.getAll(this.apiBaseUrl + 'kunde_freigabe/read.php').subscribe({
            next: response => {
                this.freigaben = response.records;
                this.ladeGeloeschteFreigaben();
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    private ladeGeloeschteFreigaben() {
        const params = new HttpParams().set('geloescht', '1');
        this.api.getAll(this.apiBaseUrl + 'kunde_freigabe/read.php', params).subscribe({
            next: response => {
                this.geloeschteFreigaben = response.records ?? [];
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    setSort(field: 'datum' | 'betrieb' | 'freigegebenFuer'): void {
        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            return;
        }
        this.sortField = field;
        this.sortDirection = field === 'datum' ? 'desc' : 'asc';
    }

    setSortField(field: 'datum' | 'betrieb' | 'freigegebenFuer'): void {
        this.sortField = field;
    }

    setSortDirection(direction: 'asc' | 'desc'): void {
        this.sortDirection = direction;
    }

    getCurrentSortLabel(): string {
        const fieldLabel =
            this.sortField === 'datum' ? 'Datum' : this.sortField === 'betrieb' ? 'Betrieb' : 'wurde freigegeben für';
        const directionLabel = this.sortDirection === 'asc' ? 'aufsteigend' : 'absteigend';
        return `${fieldLabel} (${directionLabel})`;
    }

    getMeineFreigaben(): Freigabe[] {
        return this.getFilteredAndSortedList(this.freigaben.filter(f => this.istMeineFreigabe(f)));
    }

    getFuerMichFreigaben(): Freigabe[] {
        return this.getFilteredAndSortedList(this.freigaben.filter(f => !this.istMeineFreigabe(f)));
    }

    getSortedGeloeschteFreigaben(): Freigabe[] {
        return this.getFilteredAndSortedList(this.geloeschteFreigaben);
    }

    istMeineFreigabe(f: Freigabe): boolean {
        return Number(f?.freigeber) !== 0;
    }

    getBetriebText(f: Freigabe): string {
        return this.formatBetriebLabel(f?.betrieb_freigeber, f?.zusatz_freigeber, f?.vvvo_freigeber);
    }

    getFreigegebenFuerText(f: Freigabe): string {
        return this.formatBetriebLabel(f?.betrieb_freigabe, f?.zusatz_freigabe, f?.vvvo_freigabe);
    }

    getModuleText(f: Freigabe): string {
        const parts: string[] = [];
        if (Number(f?.modul_belege) !== 0) {
            parts.push('Belege');
        }
        if (Number(f?.modul_tierbewegung) !== 0) {
            parts.push('Tierbewegung');
        }
        if (Number(f?.modul_bio) !== 0) {
            parts.push('Bio/BWL');
        }
        if (parts.length === 3) {
            return 'Alles';
        }
        return parts.length ? parts.join(', ') : 'Keine';
    }

    clearSearch(): void {
        this.searchControl.setValue('');
    }

    goBack(): void {
        window.history.back();
    }

    get isMobileScreen(): boolean {
        return this.appService.isMobileScreen;
    }

    private getFilteredAndSortedList(list: Freigabe[]): Freigabe[] {
        const query = (this.searchControl.value ?? '').toString().trim().toLowerCase();
        const filtered = (list || []).filter(item => {
            if (!query) {
                return true;
            }
            const datum = this.getDatumText(item).toLowerCase();
            const betrieb = this.getBetriebText(item).toLowerCase();
            const freigegebenFuer = this.getFreigegebenFuerText(item).toLowerCase();
            return datum.includes(query) || betrieb.includes(query) || freigegebenFuer.includes(query);
        });

        const direction = this.sortDirection === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            if (this.sortField === 'datum') {
                const aTime = new Date(a?.erstellt || 0).getTime();
                const bTime = new Date(b?.erstellt || 0).getTime();
                return (aTime - bTime) * direction;
            }
            const aValue =
                this.sortField === 'betrieb' ? this.getBetriebText(a).toLowerCase() : this.getFreigegebenFuerText(a).toLowerCase();
            const bValue =
                this.sortField === 'betrieb' ? this.getBetriebText(b).toLowerCase() : this.getFreigegebenFuerText(b).toLowerCase();
            return aValue.localeCompare(bValue) * direction;
        });
    }

    private formatBetriebLabel(name?: string, zusatz?: string, vvvo?: string | number): string {
        const n = (name || '').trim();
        const z = (zusatz || '').trim();
        const v = vvvo == null ? '' : String(vvvo).trim();
        const title = n && z ? `${n} – ${z}` : n || z;
        if (title && v) {
            return `${title} | ${v}`;
        }
        return title || v;
    }

    private getDatumText(f: Freigabe): string {
        const d = f?.erstellt ? new Date(f.erstellt) : null;
        if (!d || Number.isNaN(d.getTime())) {
            return '';
        }
        const day = `${d.getDate()}`.padStart(2, '0');
        const month = `${d.getMonth() + 1}`.padStart(2, '0');
        const year = d.getFullYear();
        return `${day}.${month}.${year}`;
    }
}
